// modules/clientes/components/ServicioInfoDialog.jsx
import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, CircularProgress, Alert, Grid,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import * as clienteService from "../services/clienteService";
import ImagenesServicioPanel from "./ImagenesServicio";
import axios from "../../../config/axiosConfig";
import { getPagoLabel } from "../constants/pago";

const MONEDA_LABEL = { 1: "USD", 2: "BOB" };

const PU_LABELS_BY_SERVICIO = {
  1: {
    pu_1: "P.U. Portatil",
    pu_2: "P.U. Movil",
    pu_4: "P.U. Base",
    pu_3: "P.U. Servicio",
  },
  2: {
    pu_1: "P.U. Alquiler",
    pu_2: "P.U. Servicio Tecnico",
    pu_4: "P.U. Servicio Nacional",
    pu_3: "P.U. Servicio Internacional",
  },
};

const getPuLabels = (codServicio) => PU_LABELS_BY_SERVICIO[Number(codServicio)] || null;

const formatMoney = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number !== 0 ? number.toFixed(2) : "-";
};

const parseMaybeJson = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("es-BO", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
};

const formatHistorialValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const getHistorialCambios = (item) => {
  const before = parseMaybeJson(item?.valores_anteriores) || {};
  const after = parseMaybeJson(item?.valores_nuevos) || {};
  if (item?.accion === "CAMBIO_CLIENTE") {
    return [
      {
        campo: "cliente",
        anterior: before.razon_social || before.cod_cliente || "-",
        nuevo: after.razon_social || after.cod_cliente || "-",
      },
    ];
  }
  const campos = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
  return campos.length
    ? campos.map((campo) => ({
      campo,
      anterior: formatHistorialValue(before[campo]),
      nuevo: formatHistorialValue(after[campo]),
    }))
    : [{ campo: "-", anterior: "-", nuevo: "-" }];
};

const ServicioInfoDialog = ({ open, cliente, servicio, onClose }) => {
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [historialError, setHistorialError] = useState(null);
  const labels = getPuLabels(servicio.cod_servicio) || {
    pu_1: "PU 1",
    pu_2: "PU 2",
    pu_3: "PU 3",
    pu_4: "PU 4",
  };

  const info = [
    ["Servicio", servicio.nombre_servicio || "-"],
    ["Grupo", servicio.grupo || servicio.grupos || "-"],
    ["Nombre", servicio.nombre || "-"],
    ["Descripcion", servicio.descripcion || "-"],
    ["Sucursal", servicio.sucursal_facturar || "-"],
    ["Pago", getPagoLabel(servicio.pago) || "-"],
    ["Moneda", MONEDA_LABEL[servicio.moneda] || servicio.moneda || "-"],
    ["Tipo", servicio.tipo || "-"],
    ["Ejecutivo", servicio.ejecutivo_venta || "-"],
    ["Correo", servicio.correo || "-"],
    ["Observacion", servicio.observacion || "-"],
  ];

  useEffect(() => {
    if (!open || !cliente?.cod_cliente || !servicio?.cod_servicio_cliente) return;
    let active = true;
    const cargarHistorial = async () => {
      setLoadingHistorial(true);
      setHistorialError(null);
      try {
        const { data } = await clienteService.getHistorialServicio(
          cliente.cod_cliente,
          servicio.cod_servicio_cliente,
        );
        const rows = data?.data || data || [];
        if (active) setHistorial(Array.isArray(rows) ? rows : []);
      } catch (err) {
        if (active) {
          setHistorial([]);
          setHistorialError(err?.response?.data?.message || "No se pudo cargar el historial");
        }
      } finally {
        if (active) setLoadingHistorial(false);
      }
    };
    cargarHistorial();
    return () => {
      active = false;
    };
  }, [open, cliente?.cod_cliente, servicio?.cod_servicio_cliente]);

  const handlePrintPdf = async () => {
    setLoadingPdf(true);
    setPdfError(null);
    try {
      const { data } = await axios.get(
        `/api/clientes/${cliente.cod_cliente}/servicios/${servicio.cod_servicio_cliente}/ficha.pdf`,
        { responseType: "blob" },
      );

      const pdfUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (err) {
      setPdfError(err?.response?.data?.message || "No se pudo generar la ficha PDF");
    } finally {
      setLoadingPdf(false);
    }
  };

  return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Informacion del servicio #{servicio.cod_servicio_cliente}</DialogTitle>
      <Divider />
      <DialogContent>
        {pdfError && <Alert severity="error" sx={{ mb: 1.5 }}>{pdfError}</Alert>}
        <Grid container spacing={1.25} sx={{ pt: 1 }}>
          {Object.entries(labels).map(([name, label]) => (
            <Grid key={name} size={{ xs: 6, sm: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
                {label}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatMoney(servicio[name])}
              </Typography>
            </Grid>
          ))}

          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 0.5 }} />
          </Grid>

          {info.map(([label, value]) => (
            <Grid key={label} size={{ xs: 12, sm: label === "Observacion" ? 12 : 6 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
                {label}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                {value}
              </Typography>
            </Grid>
          ))}
        </Grid>
        <Divider sx={{ my: 1.25 }} />
        <Typography variant="caption" sx={{ display: "block", mb: 0.75, fontWeight: 800, color: "text.secondary" }}>
          Historial de acciones
        </Typography>
        {historialError && <Alert severity="error" sx={{ mb: 1 }}>{historialError}</Alert>}
        {loadingHistorial ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">Cargando historial...</Typography>
          </Box>
        ) : historial.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            Sin acciones registradas para este servicio.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: "0.72rem", fontWeight: 800 }}>Fecha</TableCell>
                  <TableCell sx={{ fontSize: "0.72rem", fontWeight: 800 }}>Accion</TableCell>
                  <TableCell sx={{ fontSize: "0.72rem", fontWeight: 800 }}>Campo</TableCell>
                  <TableCell sx={{ fontSize: "0.72rem", fontWeight: 800 }}>Antes</TableCell>
                  <TableCell sx={{ fontSize: "0.72rem", fontWeight: 800 }}>Despues</TableCell>
                  <TableCell sx={{ fontSize: "0.72rem", fontWeight: 800 }}>Usuario</TableCell>
                  <TableCell sx={{ fontSize: "0.72rem", fontWeight: 800 }}>Observacion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historial.map((item) => (
                  <TableRow key={item.id_historial} hover>
                    <TableCell sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                      {formatDateTime(item.fecha_registro)}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                      {item.accion || "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.72rem" }}>
                      {getHistorialCambios(item).map((cambio, idx) => (
                        <Typography key={`${item.id_historial}-campo-${idx}`} variant="caption" sx={{ display: "block", fontWeight: 700 }}>
                          {cambio.campo}
                        </Typography>
                      ))}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.72rem", maxWidth: 210 }}>
                      {getHistorialCambios(item).map((cambio, idx) => (
                        <Typography key={`${item.id_historial}-antes-${idx}`} variant="caption" sx={{ display: "block", wordBreak: "break-word" }}>
                          {cambio.anterior}
                        </Typography>
                      ))}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.72rem", maxWidth: 210 }}>
                      {getHistorialCambios(item).map((cambio, idx) => (
                        <Typography key={`${item.id_historial}-despues-${idx}`} variant="caption" sx={{ display: "block", wordBreak: "break-word" }}>
                          {cambio.nuevo}
                        </Typography>
                      ))}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                      {item.usuario || "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.72rem" }}>
                      {item.observacion || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Box sx={{ mt: 2 }}>
          <ImagenesServicioPanel codCliente={cliente?.cod_cliente || servicio.cod_cliente} codServicioCliente={servicio.cod_servicio_cliente} />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          variant="contained"
          startIcon={loadingPdf ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}
          onClick={handlePrintPdf}
          disabled={loadingPdf}
        >
          {loadingPdf ? "Generando..." : "Imprimir PDF"}
        </Button>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServicioInfoDialog;
